import { FormEvent, useState } from "react";
import styled from "styled-components";
import { Auth } from "aws-amplify";
import { Link } from "react-router-dom";

import { Image } from "components/atoms";
import { AuthForm, ForgotPasswordForm } from "components/molecules";

import { MAIN_THEME } from "styles/colors";
import { FONT_FAMILY_REGULAR } from "styles/typography";

import authFormBg from "assets/images/authFormBg.png";
import logo from "assets/images/logo.svg";
import { BackgroundImageSize } from "interfaces/images";

const SignUpContainer = styled.div`
	display: flex;
	justify-content: center;
	padding: 16px 0 0;
	width: 100%;
`;

const NavLink = styled(Link)`
	color: ${MAIN_THEME.TEXT_SECONDARY};
	text-decoration: none;
	font-size: 14px;
	font-family: ${FONT_FAMILY_REGULAR};
	transition: color 0.15s ease;

	&:hover {
		color: ${MAIN_THEME.TEXT_PRIMARY};
	}
`;

const Container = styled.div`
	display: grid;
	grid-template-columns: 0.55fr 0.45fr;
	width: 100%;
	height: 100%;
	align-items: stretch;

	@media (max-width: 768px) {
		grid-template-columns: 1fr;
	}
`;

const SideImage = styled.div`
	width: 100%;
	height: 100%;
	overflow: hidden;

	@media (max-width: 768px) {
		display: none;
	}

	> div {
		height: 100%;
	}
`;

const LogoWrap = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	margin: 0 auto 32px;
	max-width: 312px;
	height: 70px;
	filter: brightness(0) invert(1);

	@media (max-width: 768px) {
		margin-bottom: 24px;
		max-width: 260px;
		height: 60px;
	}
`;

const Form = styled.div`
	width: min(420px, 100%);
`;

const FormContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	flex-direction: column;
	width: 100%;
	padding: 48px 32px;

	@media (max-width: 768px) {
		padding: 40px 20px;
	}

	@media (max-width: 480px) {
		padding: 32px 16px;
	}
`;

const ForgotPassword = (): JSX.Element => {
	const [login, setLogin] = useState("");
	const [password, setPassword] = useState("");
	const [code, setCode] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [step, setStep] = useState(1);

	const handleChangeLogin = (value: string | number) => {
		setLogin(`${value}`);
	};

	const handleChangeCode = (value: string | number) => {
		setCode(`${value}`);
	};

	const handleChangePassword = (value: string | number) => {
		setPassword(`${value}`);
	};

	const handleSubmit = async (event: FormEvent) => {
		event.preventDefault();

		try {
			setIsLoading(true);

			if (step === 1) {
				await Auth.forgotPassword(
					login // the Cognito User Object
				);

				setStep(2);
			} else {
				await Auth.forgotPasswordSubmit(login, code, password);

				await Auth.signIn(login, password);
			}

			setIsLoading(false);
		} catch (error) {
			console.log({ error });
			setIsLoading(false);
		}
	};

	return (
		<>
				<Container>
					<SideImage>
						<Image
							src={authFormBg}
							isBackground
							backgroundSize={BackgroundImageSize.COVER}
						/>
					</SideImage>

				<FormContainer>
					<Form>
							<LogoWrap>
								<Image src={logo} fullWidth />
							</LogoWrap>

						<ForgotPasswordForm
							login={login}
							password={password}
							changeLogin={handleChangeLogin}
							changePassword={handleChangePassword}
							changeCode={handleChangeCode}
							submit={handleSubmit}
							submitTitle={step === 1 ? "Send Code" : "Reset Password"}
							isLoading={isLoading}
							code={code}
							step={step}
						/>
							<SignUpContainer>
								<NavLink to="/">Back to Sign In</NavLink>
							</SignUpContainer>

					</Form>
				</FormContainer>
			</Container>
		</>
	);
};

export default ForgotPassword;
